import { BookableArea } from "@collins/react-componments/dist/types/venues";
import { DiaryContextType } from "../../../../../../context/DiaryContext";
import {
  AreaAvailability,
  Availability
} from "../../../../../../types/areaAvailability";
import {
  BookableArea as VenueBookableArea,
  BookableAreaCombination,
  BookingZone
} from "../../../../../../types/venues";
import {
  checkAreaIsOccupiedOrNot,
  occupiedAreaEndTime
} from "../../../../../../utils/booking";

export type AreaTabData = {
  freeAreas: TabData;
  allAreas: TabData;
};

type TabData = {
  areaCount: number;
  zones: ZoneData[];
};

export type ZoneData = {
  areas: AreaData[];
} & BookingZone;

export type AreaData = {
  occupied?: boolean;
  occupiedAreaEndTime?: string;
} & BookableArea &
  (
    | { combination: true; areaIds: string[] }
    | { combination?: false; areaIds?: never }
  );

const getZoneForCombination = (
  areaIds: string[],
  bookableAreas: BookableArea[]
): string | undefined => {
  const firstArea = bookableAreas.find((a) => a.id === areaIds[0]);
  return firstArea?.zone;
};

const getLatestOccupiedEndTime = (
  areaIds: string[],
  bookings: import("collins-js-sdk").ViewBooking[]
): string | undefined => {
  const times = areaIds
    .map((id) => occupiedAreaEndTime(id, bookings))
    .filter((t) => t !== "");
  if (times.length === 0) return undefined;
  const sorted = [...times].sort((a, b) => a.localeCompare(b));
  return sorted.pop();
};

const calculateCombinationMinPeople = (
  areaIds: string[],
  bookableAreas: BookableArea[]
): number => {
  const values = areaIds
    .map((id) => bookableAreas.find((a) => a.id === id)?.min_people)
    .filter((p): p is number => p !== undefined);
  return values.length > 0 ? Math.min(...values) : 0;
};

type MapCombinationProps = {
  combo: BookableAreaCombination;
  bookableAreas: VenueBookableArea[];
  bookings: import("collins-js-sdk").ViewBooking[];
  bookingTime: string;
  bookingEndTime: string;
  zoneName: string;
};

const mapCombinationToAreaData = ({
  combo,
  bookableAreas,
  bookings,
  bookingTime,
  bookingEndTime,
  zoneName
}: MapCombinationProps): AreaData => {
  const resolvedNames = combo.area_ids
    .map((id) => bookableAreas.find((a) => a.id === id)?.name)
    .filter(Boolean);
  const anyOccupied = combo.area_ids.some((id) =>
    checkAreaIsOccupiedOrNot(id, bookings, bookingTime, bookingEndTime)
  );
  return {
    id: combo.area_ids.join(","),
    name: resolvedNames.join(", "),
    zone: zoneName,
    min_people: calculateCombinationMinPeople(combo.area_ids, bookableAreas),
    max_people: combo.max_people_seated,
    max_people_seated: combo.max_people_seated,
    combination: true,
    areaIds: combo.area_ids,
    occupied: anyOccupied,
    occupiedAreaEndTime: anyOccupied
      ? getLatestOccupiedEndTime(combo.area_ids, bookings)
      : undefined
  };
};

const matchesZone = (areaZone: string | undefined, zoneId: string): boolean => {
  return areaZone === zoneId || (areaZone === undefined && zoneId === "");
};

const mapAvailabilityToAreaData = (
  area: Availability,
  zoneName: string
): AreaData => {
  const base = {
    id: area.id,
    name: area.name,
    zone: zoneName,
    min_people: area.minPeople,
    max_people: area.maxPeople,
    max_people_seated: area.maxPeople
  };
  if (area.combination && area.areaIds) {
    return { ...base, combination: true, areaIds: area.areaIds };
  }
  return base;
};

type GetAreaTabDataProps = {
  areaAvailability: AreaAvailability;
  bookingContext: DiaryContextType;
  bookingTime: string;
  bookingEndTime: string;
};
export const getAreaTabData = ({
  areaAvailability,
  bookingContext,
  bookingTime,
  bookingEndTime
}: GetAreaTabDataProps): AreaTabData => {
  const { bookings, venueConfig } = bookingContext;
  const bookableAreas = venueConfig?.bookable_areas ?? [];
  const zones = [...(venueConfig?.bookable_area_zones ?? [])];
  zones.unshift({
    id: "",
    name: "No Zone"
  });

  let freeAreasCount = 0;
  const freeZones = zones.map((zone) => {
    const areas: AreaData[] = [];

    areaAvailability?.requested?.forEach((area) => {
      if (area.combination && area.areaIds) {
        const combinationZone = getZoneForCombination(
          area.areaIds,
          bookableAreas
        );
        if (matchesZone(combinationZone, zone.id)) {
          areas.push(mapAvailabilityToAreaData(area, zone.name ?? ""));
          freeAreasCount++;
        }
      } else {
        const found = bookableAreas.find(
          (bookableArea) =>
            bookableArea.id === area.id &&
            matchesZone(bookableArea.zone, zone.id)
        );
        if (found) {
          areas.push(mapAvailabilityToAreaData(area, zone.name ?? ""));
          freeAreasCount++;
        }
      }
    });

    return { ...zone, areas };
  });

  const freeAreas = {
    areaCount: freeAreasCount,
    zones: freeZones
  };

  let allAreasCount = 0;
  const combinationEntries = venueConfig?.bookable_area_combinations ?? [];

  const allZones = zones.map((zone) => {
    const individualAreas: AreaData[] = (
      bookableAreas.filter((area) => matchesZone(area.zone, zone.id)) ?? []
    ).map((area) => ({
      ...area,
      zone: zone.name,
      occupied: checkAreaIsOccupiedOrNot(
        area.id,
        bookings ?? [],
        bookingTime,
        bookingEndTime
      ),
      occupiedAreaEndTime: occupiedAreaEndTime(area.id, bookings ?? [])
    }));

    const combinationAreas: AreaData[] = combinationEntries
      .filter((combo) => {
        const comboZone = getZoneForCombination(combo.area_ids, bookableAreas);
        return matchesZone(comboZone, zone.id);
      })
      .map((combo) =>
        mapCombinationToAreaData({
          combo,
          bookableAreas,
          bookings: bookings ?? [],
          bookingTime,
          bookingEndTime,
          zoneName: zone.name ?? ""
        })
      );

    const areas = [...individualAreas, ...combinationAreas];
    allAreasCount = allAreasCount + areas.length;
    return {
      ...zone,
      areas
    };
  });
  const allAreas = {
    areaCount: allAreasCount,
    zones: allZones
  };
  return { freeAreas, allAreas };
};
